import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { SplitView, Sizing } from 'vscode/vscode/vs/base/browser/ui/splitview/splitview';
import { Disposable, dispose } from 'vscode/vscode/vs/base/common/lifecycle';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ITerminalService, ITerminalConfigurationService, ITerminalGroupService } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal.service';
import { TerminalTabList } from './terminalTabsList.js';
import { isMacintosh } from 'vscode/vscode/vs/base/common/platform';
import { $ as $$1, getWindow, disposableWindowInterval, addDisposableListener, EventType, isActiveElement } from 'vscode/vscode/vs/base/browser/dom';
import { BrowserFeatures } from 'vscode/vscode/vs/base/browser/canIUse';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { Separator, Action } from 'vscode/vscode/vs/base/common/actions';
import { MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IMenuService } from 'vscode/vscode/vs/platform/actions/common/actions.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IContextMenuService } from 'vscode/vscode/vs/platform/contextview/browser/contextView.service';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { openContextMenu } from './terminalContextMenu.js';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { getInstanceHoverInfo } from './terminalTooltip.js';
import { IHoverService } from 'vscode/vscode/vs/platform/hover/browser/hover.service';

const _moduleId = "vs/workbench/contrib/terminal/browser/terminalTabbedView";
const $ = $$1;
let TerminalTabbedView = class TerminalTabbedView extends Disposable {
    constructor(parentElement, _terminalService, _terminalConfigurationService, _terminalGroupService, _instantiationService, _notificationService, _contextMenuService, _configurationService, menuService, _storageService, contextKeyService, _hoverService) {
        super();
        this._terminalService = _terminalService;
        this._terminalConfigurationService = _terminalConfigurationService;
        this._terminalGroupService = _terminalGroupService;
        this._instantiationService = _instantiationService;
        this._notificationService = _notificationService;
        this._contextMenuService = _contextMenuService;
        this._configurationService = _configurationService;
        this._storageService = _storageService;
        this._hoverService = _hoverService;
        this._cancelContextMenu = false;
        this._tabContainer = $('.tabs-container');
        const tabListContainer = $('.tabs-list-container');
        this._tabListElement = $('.tabs-list');
        tabListContainer.appendChild(this._tabListElement);
        this._tabContainer.appendChild(tabListContainer);
        this._instanceMenu = this._register(menuService.createMenu(MenuId.TerminalInstanceContext, contextKeyService));
        this._tabsListMenu = this._register(menuService.createMenu(MenuId.TerminalTabContext, contextKeyService));
        this._tabsListEmptyMenu = this._register(menuService.createMenu(MenuId.TerminalTabEmptyAreaContext, contextKeyService));
        this._tabList = this._register(this._instantiationService.createInstance(TerminalTabList, this._tabListElement));
        const terminalOuterContainer = $('.terminal-outer-container');
        this._terminalContainer = $('.terminal-groups-container');
        terminalOuterContainer.appendChild(this._terminalContainer);
        this._terminalService.setContainers(parentElement, this._terminalContainer);
        this._terminalIsTabsNarrowContextKey = TerminalContextKeys.tabsNarrow.bindTo(contextKeyService);
        this._terminalTabsFocusContextKey = TerminalContextKeys.tabsFocus.bindTo(contextKeyService);
        this._terminalTabsMouseContextKey = TerminalContextKeys.tabsMouse.bindTo(contextKeyService);
        this._tabTreeIndex = this._terminalConfigurationService.config.tabs.location === 'left' ? 0 : 1;
        this._terminalContainerIndex = this._terminalConfigurationService.config.tabs.location === 'left' ? 1 : 0;
        this._register(_configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration("terminal.integrated.tabs.enabled" ) ||
                e.affectsConfiguration("terminal.integrated.tabs.hideCondition" )) {
                this._refreshShowTabs();
            }
            else if (e.affectsConfiguration("terminal.integrated.tabs.location" )) {
                this._tabTreeIndex = this._terminalConfigurationService.config.tabs.location === 'left' ? 0 : 1;
                this._terminalContainerIndex = this._terminalConfigurationService.config.tabs.location === 'left' ? 1 : 0;
                if (this._shouldShowTabs()) {
                    this._splitView.swapViews(0, 1);
                    this._removeSashListener();
                    this._addSashListener();
                    this._splitView.resizeView(this._tabTreeIndex, this._getLastListWidth());
                }
            }
        }));
        this._register(this._terminalGroupService.onDidChangeInstances(() => this._refreshShowTabs()));
        this._register(this._terminalGroupService.onDidChangeGroups(() => this._refreshShowTabs()));
        this._attachEventListeners(parentElement, this._terminalContainer);
        this._register(this._terminalGroupService.onDidChangePanelOrientation((orientation) => {
            this._panelOrientation = orientation;
            if (this._panelOrientation === 0 ) {
                this._terminalContainer.classList.add("terminal-side-view" );
            }
            else {
                this._terminalContainer.classList.remove("terminal-side-view" );
            }
        }));
        this._splitView = ( (new SplitView(
            parentElement,
            { orientation: 1 , proportionalLayout: false }
        )));
        this._setupSplitView(terminalOuterContainer);
    }
    _shouldShowTabs() {
        const enabled = this._terminalConfigurationService.config.tabs.enabled;
        const hide = this._terminalConfigurationService.config.tabs.hideCondition;
        if (!enabled) {
            return false;
        }
        if (hide === 'never') {
            return true;
        }
        if (hide === 'singleTerminal' && this._terminalGroupService.instances.length > 1) {
            return true;
        }
        if (hide === 'singleGroup' && this._terminalGroupService.groups.length > 1) {
            return true;
        }
        return false;
    }
    _refreshShowTabs() {
        if (this._shouldShowTabs()) {
            if (this._splitView.length === 1) {
                this._addTabTree();
                this._addSashListener();
                this._splitView.resizeView(this._tabTreeIndex, this._getLastListWidth());
                this.rerenderTabs();
            }
        }
        else {
            if (this._splitView.length === 2 && !this._terminalTabsMouseContextKey.get()) {
                this._splitView.removeView(this._tabTreeIndex);
                if (this._plusButton) {
                    this._tabContainer.removeChild(this._plusButton);
                }
                this._removeSashListener();
            }
        }
    }
    _getLastListWidth() {
        const widthKey = this._panelOrientation === 0  ? "tabs-list-width-vertical"  : "tabs-list-width-horizontal" ;
        const storedValue = this._storageService.get(widthKey, 0 );
        if (!storedValue || !parseInt(storedValue)) {
            return this._panelOrientation === 0  ? 46  : 120 ;
        }
        return parseInt(storedValue);
    }
    _handleOnDidSashReset() {
        let idealWidth = 80 ;
        const offscreenCanvas = document.createElement('canvas');
        offscreenCanvas.width = 1;
        offscreenCanvas.height = 1;
        const ctx = offscreenCanvas.getContext('2d');
        if (ctx) {
            const style = getWindow(this._tabListElement).getComputedStyle(this._tabListElement);
            ctx.font = `${style.fontStyle} ${style.fontSize} ${style.fontFamily}`;
            const maxInstanceWidth = this._terminalGroupService.instances.reduce((p, c) => {
                return Math.max(p, ctx.measureText(c.title + (c.description || '')).width + this._getAdditionalWidth(c));
            }, 0);
            idealWidth = Math.ceil(Math.max(maxInstanceWidth, 80 ));
        }
        const currentWidth = Math.ceil(this._splitView.getViewSize(this._tabTreeIndex));
        if (currentWidth === idealWidth) {
            idealWidth = 46 ;
        }
        this._splitView.resizeView(this._tabTreeIndex, idealWidth);
        this._updateListWidth(idealWidth);
    }
    _getAdditionalWidth(instance) {
        const additionalWidth = 40;
        const statusIconWidth = instance.statusList.statuses.length > 0 ? 30  : 0;
        const splitAnnotationWidth = (this._terminalGroupService.getGroupForInstance(instance)?.terminalInstances.length || 0) > 1 ? 30  : 0;
        return additionalWidth + splitAnnotationWidth + statusIconWidth;
    }
    _handleOnDidSashChange() {
        const listWidth = this._splitView.getViewSize(this._tabTreeIndex);
        if (!this._width || listWidth <= 0) {
            return;
        }
        this._updateListWidth(listWidth);
    }
    _updateListWidth(width) {
        if (width < 63  && width >= 46 ) {
            width = 46 ;
            this._splitView.resizeView(this._tabTreeIndex, width);
        }
        else if (width >= 63  && width < 80 ) {
            width = 80 ;
            this._splitView.resizeView(this._tabTreeIndex, width);
        }
        this.rerenderTabs();
        const widthKey = this._panelOrientation === 0  ? "tabs-list-width-vertical"  : "tabs-list-width-horizontal" ;
        this._storageService.store(widthKey, width, 0 , 0 );
    }
    _setupSplitView(terminalOuterContainer) {
        this._register(this._splitView.onDidSashReset(() => this._handleOnDidSashReset()));
        this._register(this._splitView.onDidSashChange(() => this._handleOnDidSashChange()));
        if (this._shouldShowTabs()) {
            this._addTabTree();
        }
        this._splitView.addView({
            element: terminalOuterContainer,
            layout: width => this._terminalGroupService.groups.forEach(tab => tab.layout(width, this._height || 0)),
            minimumSize: 120,
            maximumSize: Number.POSITIVE_INFINITY,
            onDidChange: () => Disposable.None,
            priority: 2
        }, Sizing.Distribute, this._terminalContainerIndex);
        if (this._shouldShowTabs()) {
            this._addSashListener();
        }
    }
    _addTabTree() {
        this._splitView.addView({
            element: this._tabContainer,
            layout: width => this._tabList.layout(this._height || 0, width),
            minimumSize: 46 ,
            maximumSize: 500 ,
            onDidChange: () => Disposable.None,
            priority: 1
        }, Sizing.Distribute, this._tabTreeIndex);
        this.rerenderTabs();
    }
    rerenderTabs() {
        this._updateHasText();
        this._tabList.refresh();
    }
    _addSashListener() {
        let interval;
        this._sashDisposables = [
            this._splitView.sashes[0].onDidStart(e => {
                interval = disposableWindowInterval(getWindow(this._splitView.el), () => {
                    this.rerenderTabs();
                }, 100);
            }),
            this._splitView.sashes[0].onDidEnd(e => {
                interval.dispose();
            })
        ];
    }
    _removeSashListener() {
        if (this._sashDisposables) {
            dispose(this._sashDisposables);
            this._sashDisposables = undefined;
        }
    }
    _updateHasText() {
        const hasText = this._tabListElement.clientWidth > 63 ;
        this._tabContainer.classList.toggle('has-text', hasText);
        this._terminalIsTabsNarrowContextKey.set(!hasText);
    }
    layout(width, height) {
        this._height = height;
        this._width = width;
        this._splitView.layout(width);
        if (this._shouldShowTabs()) {
            this._splitView.resizeView(this._tabTreeIndex, this._getLastListWidth());
        }
        this._updateHasText();
    }
    _attachEventListeners(parentDomElement, terminalContainer) {
        this._register(addDisposableListener(this._tabContainer, 'mouseleave', async (event) => {
            this._terminalTabsMouseContextKey.set(false);
            this._refreshShowTabs();
            event.stopPropagation();
        }));
        this._register(addDisposableListener(this._tabContainer, 'mouseenter', async (event) => {
            this._terminalTabsMouseContextKey.set(true);
            event.stopPropagation();
        }));
        this._register(addDisposableListener(terminalContainer, 'mousedown', async (event) => {
            const terminal = this._terminalGroupService.activeInstance;
            if (this._terminalGroupService.instances.length === 0 || !terminal) {
                this._cancelContextMenu = true;
                return;
            }
            if (event.which === 2) {
                switch (this._terminalConfigurationService.config.middleClickBehavior) {
                    case 'paste':
                        terminal.paste();
                        break;
                    case 'default':
                    default:
                        terminal.focus();
                        break;
                }
            }
            else if (event.which === 3) {
                const rightClickBehavior = this._terminalConfigurationService.config.rightClickBehavior;
                if (rightClickBehavior === 'nothing') {
                    if (!event.shiftKey) {
                        this._cancelContextMenu = true;
                    }
                    return;
                }
                else if (rightClickBehavior === 'copyPaste' || rightClickBehavior === 'paste') {
                    if (rightClickBehavior === 'copyPaste' && event.shiftKey) {
                        openContextMenu(getWindow(terminalContainer), event, terminal, this._instanceMenu, this._contextMenuService);
                        return;
                    }
                    if (rightClickBehavior === 'copyPaste' && terminal.hasSelection()) {
                        await terminal.copySelection();
                        terminal.clearSelection();
                    }
                    else {
                        if (BrowserFeatures.clipboard.readText) {
                            terminal.paste();
                        }
                        else {
                            this._notificationService.info(`This browser doesn't support the clipboard.readText API needed to trigger a paste, try ${isMacintosh ? '⌘' : 'Ctrl'}+V instead.`);
                        }
                    }
                    if (isMacintosh) {
                        setTimeout(() => {
                            terminal.clearSelection();
                        }, 0);
                    }
                    this._cancelContextMenu = true;
                }
            }
        }));
        this._register(addDisposableListener(terminalContainer, 'contextmenu', (event) => {
            const rightClickBehavior = this._terminalConfigurationService.config.rightClickBehavior;
            if (rightClickBehavior === 'nothing' && !event.shiftKey) {
                this._cancelContextMenu = true;
            }
            terminalContainer.focus();
            if (!this._cancelContextMenu) {
                openContextMenu(getWindow(terminalContainer), event, this._terminalGroupService.activeInstance, this._instanceMenu, this._contextMenuService);
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            this._cancelContextMenu = false;
        }));
        this._register(addDisposableListener(this._tabContainer, 'contextmenu', (event) => {
            const rightClickBehavior = this._terminalConfigurationService.config.rightClickBehavior;
            if (rightClickBehavior === 'nothing' && !event.shiftKey) {
                this._cancelContextMenu = true;
            }
            if (!this._cancelContextMenu) {
                const emptyList = this._tabList.getFocus().length === 0;
                if (!emptyList) {
                    this._terminalGroupService.lastAccessedMenu = 'tab-list';
                }
                const selectedInstances = this._tabList.getSelectedElements();
                const focusedInstance = this._tabList.getFocusedElements()?.[0];
                if (focusedInstance) {
                    selectedInstances.splice(selectedInstances.findIndex(e => e.instanceId === focusedInstance.instanceId), 1);
                    selectedInstances.unshift(focusedInstance);
                }
                openContextMenu(getWindow(this._tabContainer), event, selectedInstances, emptyList ? this._tabsListEmptyMenu : this._tabsListMenu, this._contextMenuService, emptyList ? this._getTabActions() : undefined);
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            this._cancelContextMenu = false;
        }));
        this._register(addDisposableListener(terminalContainer.ownerDocument, 'keydown', (event) => {
            terminalContainer.classList.toggle('alt-active', !!event.altKey);
        }));
        this._register(addDisposableListener(terminalContainer.ownerDocument, 'keyup', (event) => {
            terminalContainer.classList.toggle('alt-active', !!event.altKey);
        }));
        this._register(addDisposableListener(parentDomElement, 'keyup', (event) => {
            if (event.keyCode === 27) {
                event.stopPropagation();
            }
        }));
        this._register(addDisposableListener(this._tabContainer, EventType.FOCUS_IN, () => {
            this._terminalTabsFocusContextKey.set(true);
        }));
        this._register(addDisposableListener(this._tabContainer, EventType.FOCUS_OUT, () => {
            this._terminalTabsFocusContextKey.set(false);
        }));
    }
    _getTabActions() {
        return [
            ( (new Separator())),
            this._configurationService.inspect("terminal.integrated.tabs.location" ).userValue === 'left' ?
                ( (new Action('moveRight', ( localizeWithPath(_moduleId, 0, "Move Tabs Right")), undefined, undefined, async () => {
                this._configurationService.updateValue("terminal.integrated.tabs.location" , 'right');
            }))) :
                ( (new Action('moveLeft', ( localizeWithPath(_moduleId, 1, "Move Tabs Left")), undefined, undefined, async () => {
                this._configurationService.updateValue("terminal.integrated.tabs.location" , 'left');
            }))),
            ( (new Action('hideTabs', ( localizeWithPath(_moduleId, 2, "Hide Tabs")), undefined, undefined, async () => {
            this._configurationService.updateValue("terminal.integrated.tabs.enabled" , false);
        })))
        ];
    }
    setEditable(isEditing) {
        if (!isEditing) {
            this._tabList.domFocus();
        }
        this._tabList.refresh(false);
    }
    focusTabs() {
        if (!this._shouldShowTabs()) {
            return;
        }
        this._terminalTabsFocusContextKey.set(true);
        const selected = this._tabList.getSelection();
        this._tabList.domFocus();
        if (selected) {
            this._tabList.setFocus(selected);
        }
    }
    focus() {
        if (this._terminalService.connectionState === 1 ) {
            this._focus();
            return;
        }
        const previousActiveElement = this._tabListElement.ownerDocument.activeElement;
        if (previousActiveElement) {
            this._register(this._terminalService.onDidChangeConnectionState(() => {
                if (isActiveElement(previousActiveElement)) {
                    this._focus();
                }
            }));
        }
    }
    focusHover() {
        if (this._shouldShowTabs()) {
            this._tabList.focusHover();
            return;
        }
        const instance = this._terminalGroupService.activeInstance;
        if (!instance) {
            return;
        }
        this._hoverService.showHover({
            ...getInstanceHoverInfo(instance),
            target: this._terminalContainer,
            trapFocus: true
        }, true);
    }
    _focus() {
        this._terminalGroupService.activeInstance?.focusWhenReady();
    }
};
TerminalTabbedView = ( (__decorate([
    ( (__param(1, ITerminalService))),
    ( (__param(2, ITerminalConfigurationService))),
    ( (__param(3, ITerminalGroupService))),
    ( (__param(4, IInstantiationService))),
    ( (__param(5, INotificationService))),
    ( (__param(6, IContextMenuService))),
    ( (__param(7, IConfigurationService))),
    ( (__param(8, IMenuService))),
    ( (__param(9, IStorageService))),
    ( (__param(10, IContextKeyService))),
    ( (__param(11, IHoverService)))
], TerminalTabbedView)));

export { TerminalTabbedView };
